import { getMe } from '@/api/generated/auth/auth';
import { sseEmitter } from './sseEmitter';
import { SSE_EVENT_TYPES, type SseEventMap, type SseServerEventType } from './sseTypes';

/**
 * Singleton SSE transport (backend.12.realtime.md): jediné `EventSource` spojení per tab na
 * `GET /api/sse/events`; příchozí pojmenované eventy jen přeposílá do typovaného `sseEmitter`.
 * Transport nikdy neroste s novými event typy — dispatch je řízený `SSE_EVENT_TYPES`.
 *
 * VÝJIMKA z pravidla „žádný vlastní HTTP klient" (GUIDELINES §7): SSE stream (`text/event-stream`)
 * nelze vyjádřit v OpenAPI ani obsloužit axiosem — nativní `EventSource` je jediná cesta.
 * REST volání jdou dál výhradně přes Orval (auth probe níže používá vygenerované `getMe`).
 */

const SSE_URL = '/api/sse/events';
const RECONNECT_MIN_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;

let eventSource: EventSource | null = null;
let reconnectTimer: number | null = null;
let reconnectDelayMs = RECONNECT_MIN_MS;
let hasConnectedBefore = false;
let enabled = false;

function scheduleReconnect(): void {
  if (!enabled || reconnectTimer !== null) return;
  const delay = reconnectDelayMs;
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, RECONNECT_MAX_MS);
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    void reconnectAfterAuthProbe();
  }, delay);
}

/**
 * Server spojení tvrdě ukončil (typicky 401 po expiraci session). Před dalším pokusem probni auth
 * přes běžné REST volání — případná 401 projde axios interceptorem AuthProvideru, který session
 * obnoví nebo přepne na LoginGate. Samotný výsledek probe nerozhoduje (connect se zkusí vždy).
 */
async function reconnectAfterAuthProbe(): Promise<void> {
  if (!enabled) return;
  try {
    await getMe();
  } catch {
    // 401 / síťová chyba — řeší AuthProvider interceptor; reconnect se naplánuje z onerror.
  }
  openConnection();
}

function openConnection(): void {
  if (!enabled || eventSource !== null) return;

  const source = new EventSource(SSE_URL, { withCredentials: true });
  eventSource = source;

  source.onopen = () => {
    reconnectDelayMs = RECONNECT_MIN_MS;
    const reconnected = hasConnectedBefore;
    hasConnectedBefore = true;
    sseEmitter.emit('sse:connected', { reconnected });
  };

  source.onerror = () => {
    // CONNECTING = nativní auto-reconnect EventSource běží sám. CLOSED = server spojení odmítl
    // (401, restart) → manuální reconnect s backoffem + auth probe.
    if (source.readyState === EventSource.CLOSED) {
      if (eventSource === source) eventSource = null;
      source.close();
      scheduleReconnect();
    }
  };

  for (const type of SSE_EVENT_TYPES) {
    source.addEventListener(type, (event) => {
      dispatchServerEvent(type, (event as MessageEvent<string>).data);
    });
  }
}

function dispatchServerEvent(type: SseServerEventType, data: string): void {
  let payload: SseEventMap[SseServerEventType];
  try {
    payload = JSON.parse(data) as SseEventMap[SseServerEventType];
  } catch {
    // Poškozený frame — zahodit; stav dorovná refetch po dalším `sse:connected`.
    return;
  }
  sseEmitter.emit(type, payload as never);
}

/** Otevře SSE spojení (idempotentní — další volání jsou no-op). Volat po přihlášení. */
export function connectSse(): void {
  if (enabled) return;
  enabled = true;
  openConnection();
}

/** Zavře SSE spojení a zruší naplánované reconnecty. Volat při odhlášení/unmountu shellu. */
export function disconnectSse(): void {
  enabled = false;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  eventSource?.close();
  eventSource = null;
  hasConnectedBefore = false;
  reconnectDelayMs = RECONNECT_MIN_MS;
}
